import { Button } from '@workspace/ui/components/button';
import { ArrowRight, Info } from 'lucide-react';
import { useState } from 'react';

import { m } from '~/paraglide/messages.js';

import type { TypeOfOrganizationData } from '../-schemas/org-schema';

type Props = {
  whereAreYouStart: string;
  values: TypeOfOrganizationData;
  onUpdate: (data: TypeOfOrganizationData) => void;
  onNext: (organizationType: string) => void;
};

type OrganizationType = {
  id: string;
  name: string;
  description: string;
  isPrimary?: boolean;
  countries: string[];
};

const ORGANIZATION_TYPES: OrganizationType[] = [
  {
    id: 'sas',
    name: m['organization.type_org.sas_name'](),
    description: m['organization.type_org.sas_description'](),
    countries: ['colombia'],
  },
  {
    id: 'sa',
    name: m['organization.type_org.sa_name'](),
    description: m['organization.type_org.sa_description'](),
    countries: ['colombia'],
  },
  {
    id: 'llc',
    name: m['organization.type_org.llc_name'](),
    description: m['organization.type_org.llc_description'](),
    countries: ['usa'],
  },
  {
    id: 'corp',
    name: m['organization.type_org.corp_name'](),
    description: m['organization.type_org.corp_description'](),
    countries: ['usa'],
  },
  {
    id: 'dao',
    name: m['organization.type_org.dao_name'](),
    description: 'organization.type_org.dao_description',
    countries: ['global'],
  },
];

type OrganizationCardProps = {
  organization: OrganizationType;
  isSelected: boolean;
  onClick: () => void;
};

function OrganizationCard({
  organization,
  isSelected,
  onClick,
}: OrganizationCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group relative flex flex-col items-start gap-2 rounded-xl border-2 p-6 text-left transition-all hover:bg-gray-900/40 focus:outline-none focus:ring-2 ${
        isSelected
          ? 'border-[#e4e4e7] bg-[#e4e4e7]/10'
          : organization.isPrimary
            ? 'border-gray-700 bg-gray-900/30 hover:border-gray-600'
            : 'border-gray-800 bg-gray-900/30 hover:border-gray-700'
      }`}
    >
      {/* Info icon */}
      <div className="absolute right-4 top-4">
        <Info className="h-4 w-4 text-gray-500" />
      </div>

      {/* Organization name */}
      <h3 className="text-2xl font-bold text-white">{organization.name}</h3>

      {/* Description */}
      <p className="text-sm text-gray-400">{organization.description}</p>
    </button>
  );
}

export function TypeOfOrganizationComponent({
  whereAreYouStart,
  values,
  onUpdate,
  onNext,
}: Props) {
  const [selectedType, setSelectedType] = useState<string | null>(
    values.type || null,
  );

  const filteredOrganizationTypes = ORGANIZATION_TYPES.filter((org) =>
    org.countries.includes(whereAreYouStart.toLowerCase()),
  );

  const handleContinue = () => {
    if (selectedType) {
      onUpdate({ type: selectedType });
      onNext(selectedType);
    }
  };

  return (
    <div className="space-y-6">
      {/* Organization types grid */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {filteredOrganizationTypes.map((org) => (
          <OrganizationCard
            key={org.id}
            organization={org}
            isSelected={selectedType === org.id}
            onClick={() => setSelectedType(org.id)}
          />
        ))}
      </div>

      <Button
        onClick={handleContinue}
        disabled={!selectedType}
        className="flex w-full items-center justify-center gap-2 rounded-lg px-6 py-6 font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-50"
      >
        {m['organization.type_org.continue']()}
        <ArrowRight className="h-5 w-5" />
      </Button>
    </div>
  );
}
